import { PropsWithChildren, useMemo } from 'react';
import { useToggle } from '../../../../common/hooks/useToggle';
import { useMobile } from '../../../../common/services/screenSize/ScreenSizeService';
import { ChildrenOnly } from '../../../../common/types/ChildrenOnly';
import { Contents, DisclosureButton } from './Disclosure.styles';

const WORDS_VISIBLE_ON_MOBILE = 22;
const EXTRA_WORDS_VISIBLE_ON_DESKTOP = 44;
type Props = PropsWithChildren<{
  alwaysOpen?: boolean;
}>;

const Disclosure = ({ children, alwaysOpen }: Props) => {
  const [disclosed, toggleDisclosed] = useToggle(false);
  const shown = Boolean(disclosed || alwaysOpen);
  return (
    <>
      {!shown && (
        <>
          ...<DisclosureButton onClick={toggleDisclosed}>Read more</DisclosureButton>
        </>
      )}
      <Contents shown={shown}>{children}</Contents>
    </>
  );
};

export const MobileDisclosure = ({ children }: ChildrenOnly) => (
  <Disclosure alwaysOpen={!useMobile()}>{children}</Disclosure>
);

export const DesktopDisclosure = ({ children }: ChildrenOnly) => (
  <Disclosure alwaysOpen={useMobile()}>{children}</Disclosure>
);

export const AutoDisclosure = ({ children }: { children: string }) => {
  const words = useMemo(() => children.split(' '), [children]);
  const alwaysVisible = words.slice(0, WORDS_VISIBLE_ON_MOBILE).join(' ');

  const hiddenOnMobile = words
    .slice(WORDS_VISIBLE_ON_MOBILE, WORDS_VISIBLE_ON_MOBILE + EXTRA_WORDS_VISIBLE_ON_DESKTOP)
    .join(' ');

  const hiddenOnDesktop = words
    .slice(WORDS_VISIBLE_ON_MOBILE + EXTRA_WORDS_VISIBLE_ON_DESKTOP)
    .join(' ');

  return (
    <>
      {alwaysVisible}{' '}
      {hiddenOnMobile && (
        <MobileDisclosure>
          {hiddenOnMobile}{' '}
          {hiddenOnDesktop && <DesktopDisclosure>{hiddenOnDesktop}</DesktopDisclosure>}
        </MobileDisclosure>
      )}
    </>
  );
};
